/**
 * TileInfoButton — small click-popover affordance for "what is this?"
 * explanations next to a card title, metric label, or status pill.
 *
 * Distinct from a hover tooltip: this is for the slower, more
 * deliberate read where a user actually wants the longer paragraph
 * + an optional deep-link into the source module. Use a hover
 * Tooltip for terse <2-line hints; use this for "explain it like I'm
 * a CFO who just landed on the page" content.
 *
 * Two visual tones:
 *   - tone="light" (default): for white/light backgrounds
 *   - tone="dark":            for dark hero / pill backgrounds (uses
 *                             white/translucent button colors)
 */
import React from 'react';
import { Info, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

export function TileInfoButton({
  testid,
  title,
  body,
  linkLabel,
  onLink,
  tone = 'light',
  side = 'top',
  align = 'start',
}) {
  const dark = tone === 'dark';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'p-0.5 rounded-full transition-colors flex-shrink-0',
            dark
              ? 'text-white/60 hover:text-white hover:bg-white/15'
              : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100',
          )}
          aria-label={`What is ${title}?`}
          data-testid={testid}
          onClick={(e) => e.stopPropagation()}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-80 text-xs leading-relaxed text-slate-700"
        style={{ zIndex: 80 }}
        data-testid={`${testid}-content`}
      >
        <div className="font-semibold text-slate-900 text-sm mb-1">{title}</div>
        <p className="text-slate-600">{body}</p>
        {linkLabel && onLink && (
          <button
            type="button"
            onClick={onLink}
            className="mt-2 inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium"
            data-testid={`${testid}-link`}
          >
            {linkLabel}
            <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default TileInfoButton;
